// Normalises markdown tables before rendering so figures are ALWAYS right-aligned.
//
// remark-gfm only right-aligns a column when the separator row carries `---:`,
// and the model omits that often enough to be noticeable — the same defect the
// Word export had. This mirrors the server-side rule in
// el-bank-backend/src/controller/word_document.py so the chat and the downloaded
// document agree: a column whose values are figures is right-aligned regardless
// of what the markdown claimed.

// "1'234.5", "-3,0%", "−5,3 %" (U+2212), "CHF 850", "832.2 Tsd.", "1.2 Mio",
// "2,97 Mrd. USD", "191.8m USD", "46,6 bps", "18.0%" — same pattern as
// _NUMERIC_CELL_RE in el-bank-backend/src/controller/word_document.py.
const CURRENCY = '(?:CHF|EUR|USD|GBP|GBp|SEK|DKK|NOK|PLN)';
const UNIT = '(?:Tsd\\.?|Mio\\.?|Mrd\\.?|Md|mila|mln|mld|mil(?:\\s?M)?|bn|bps|bp|Bp|[KMkm])';
const NUMERIC_CELL = new RegExp(
  `^\\(?\\s*[-+–—−]?\\s*(?:${CURRENCY}\\s*)?[-+–—−]?\\d[\\d\\s'’.,\u00a0\u202f]*`
  + `(?:\\s*${UNIT})?(?:\\s*(?:%|${CURRENCY}))?\\s*\\)?$`,
);

// Dates and bare years read as figures in a financial table.
const DATE_CELL = /^\s*(?:\d{4}-\d{2}-\d{2}|\d{1,2}\.\d{1,2}\.\d{2,4}|\d{1,2}\/\d{1,2}\/\d{2,4}|\d{4})\s*$/;

const SEPARATOR_CELL = /^:?-{2,}:?$/;

const splitRow = (line) =>
  line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());

const isSeparatorRow = (cells) =>
  cells.length > 0 && cells.every((cell) => SEPARATOR_CELL.test(cell.replace(/\s/g, '')));

const isFigure = (value) => NUMERIC_CELL.test(value) || DATE_CELL.test(value);

// A column counts as numeric when more than half of its non-empty values are figures.
function numericColumns(dataRows, columnCount) {
  const numeric = [];
  for (let col = 0; col < columnCount; col += 1) {
    const values = dataRows
      .map((row) => (row[col] || '').trim())
      .filter((value) => value && !['-', '–', '—'].includes(value));
    const hits = values.filter(isFigure).length;
    numeric.push(values.length > 0 && hits > values.length / 2);
  }
  return numeric;
}

/**
 * Rewrites the separator row of every markdown table so numeric columns use `---:`.
 * Non-table content is returned untouched.
 */
export function alignNumericColumns(markdown) {
  if (!markdown || markdown.indexOf('|') === -1) return markdown;

  const lines = markdown.split('\n');
  const out = [...lines];

  for (let i = 0; i < lines.length; i += 1) {
    const cells = splitRow(lines[i]);
    if (!lines[i].includes('|') || !isSeparatorRow(cells)) continue;

    // Collect the contiguous data rows that follow this separator.
    const dataRows = [];
    for (let j = i + 1; j < lines.length; j += 1) {
      if (!lines[j].includes('|')) break;
      const row = splitRow(lines[j]);
      if (isSeparatorRow(row)) break;
      dataRows.push(row);
    }
    if (dataRows.length === 0) continue;

    const numeric = numericColumns(dataRows, cells.length);
    if (!numeric.some(Boolean)) continue;

    out[i] = `|${cells
      .map((cell, col) => {
        if (!numeric[col]) return cell;
        const token = cell.replace(/\s/g, '');
        // Keep an explicit centre marker; otherwise force right alignment.
        if (token.startsWith(':') && token.endsWith(':')) return cell;
        return '---:';
      })
      .join('|')}|`;
  }

  return out.join('\n');
}

export default alignNumericColumns;
